import type { ReactNode } from 'react';
import { Undo2 } from 'lucide-react';
import { renderLiteMarkdown } from '@/lib/markdownLite';
import { splitDraftReply, looksLikeDocument as looksLikeDocumentDefault, MANUAL_MARKER } from '@/lib/aiDraftCleanup';
import { cn } from '@/lib/cn';
import type { SectionChange } from './documentSections';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  doc_type?: string;
}

const renderMsg = (content: string): ReactNode => renderLiteMarkdown(content);

// A compact bubble of our own, rather than the shared .msg-bubble-ai/-user
// (index.css — 10px/14px padding, 13px type, 85% max-width): those sizes
// were tuned for the old, wider chat panel, and read as oversized inside
// this column's ~300px of usable width. Left untouched for every other
// caller (the standalone ConsultationModule path, DischargeSummaryChat).
const BUBBLE = 'max-w-[94%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[12.5px] leading-snug';

/**
 * v3 conversational layout — the always-visible turn list inside
 * `AssistantColumn`, replacing the old "Conversation (n) ▸" collapse
 * (docs/design/consultation/Conversation.dc.html marker 5). Ported from
 * ConsultationChat.tsx's own `transcriptBody`, embedded-only: the
 * standalone `ConsultationModule` path keeps its original inline rendering.
 *
 * An AI turn never repeats the document text (wireframe annotation: "AI
 * turn = what it did + jump chips, never the document text") — it shows
 * only what the AI said, plus, for a turn that changed the document,
 * "↧ <Section>" jump chips (scrolls the note there) and, on the most
 * recent such turn only, an Undo chip (see ConsultationChat.tsx for why
 * undo is single-level). There is deliberately no "use this reply as the
 * document" override here: the note canvas is always the live document,
 * updated by the AI turn itself — a second, manual promote-to-document
 * action would contradict that and was removed.
 */
export function ConsultationTranscript({
  msgs, msgsLoading, loading, streamingText,
  changesByIndex, latestChangeIndex, onJump, onUndoTurn,
  isDocumentText = looksLikeDocumentDefault,
}: {
  msgs: ChatMessage[];
  msgsLoading: boolean;
  loading: boolean;
  streamingText: string | null;
  changesByIndex: Map<number, SectionChange[]>;
  latestChangeIndex: number | null;
  onJump: (key: string) => void;
  onUndoTurn: () => void;
  /** Marker-less fallback detector for "this reply IS the document" (see the
   * unmarked-doc comment below) — aiDraftCleanup.ts's own `looksLikeDocument`
   * only recognizes Consultation Notes' SOAP vocabulary (subjective/
   * assessment/plan) plus Rx./DISCHARGE, so a Discharge Summary reply with no
   * marker (common on its very first, silently-triggered turn) read as plain
   * chat and got dumped whole into the bubble. DischargeSummaryChat.tsx
   * passes its own, differently-worded detector; every other caller keeps
   * the shared default. */
  isDocumentText?: (text: string) => boolean;
}) {
  return (
    <>
      {msgsLoading && <div className="py-5 text-center text-[13px] text-ink-muted">Loading conversation…</div>}
      {msgs.map((m, i) => {
        // A reply carrying a draft shows only what the AI said to the
        // doctor; the draft itself is in the note canvas, never repeated
        // here. Same for a doctor-authored draft. The model doesn't always
        // comply with the "--- AI-ASSISTED DRAFT ---" marker (see
        // aiDraftCleanup.ts's own header comment on looksLikeDocument) —
        // when it's missing but the reply is still plainly the whole
        // document (no separate chat text), fall back to the same generic
        // line rather than dumping the raw document into the bubble too.
        const isManual = m.role === 'assistant' && m.content.includes(MANUAL_MARKER);
        const split = m.role === 'assistant' && !isManual ? splitDraftReply(m.content) : { chat: m.content, doc: null };
        const isUnmarkedDoc = m.role === 'assistant' && !isManual && split.doc === null && isDocumentText(m.content);
        const changes = changesByIndex.get(i);
        return (
          <div key={i} className={cn('flex flex-col gap-1', m.role === 'user' ? 'items-end' : 'items-start')}>
            <div className={cn(BUBBLE, m.role === 'user' ? 'rounded-br-sm bg-teal text-white' : 'rounded-bl-sm bg-muted text-ink')}>
              {m.role === 'user' ? m.content
                : isManual ? <span className="text-[12.5px]">You wrote the draft yourself — it's in the note.</span>
                : split.doc !== null ? renderMsg(split.chat || "I've updated the draft — review it in the note.")
                : isUnmarkedDoc ? <span className="text-[12.5px]">I've updated the draft — review it in the note.</span>
                : renderMsg(m.content)}
            </div>
            {changes && changes.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                {changes.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => onJump(c.key)}
                    className="rounded border border-border px-1.5 py-0.5 text-[10.5px] text-ink-muted hover:border-teal hover:text-teal"
                  >
                    ↧ {c.heading}
                  </button>
                ))}
                {i === latestChangeIndex && (
                  <button
                    type="button"
                    onClick={onUndoTurn}
                    className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10.5px] text-ink-muted hover:border-teal hover:text-teal"
                  >
                    <Undo2 className="h-3 w-3" aria-hidden="true" /> undo
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {/* The in-progress turn shows only the AI's own conversational text as
          it streams in, if any — never a "writing…"/"generating…" caption
          trailing it. The document itself streams straight into the note
          canvas, and the assistant column's own status line (in
          AssistantColumn, between this transcript and the composer) is
          where progress is reported; repeating it here as a second, bubble-
          shaped caption was exactly the clutter this transcript is meant to
          avoid. Nothing renders at all while the model has produced no
          chat text yet (e.g. writing straight into the note with no
          preamble) — the status line alone carries that. */}
      {loading && streamingText && (() => {
        const live = splitDraftReply(streamingText);
        // Same unmarked-document fallback as the message list above — once
        // the in-progress reply itself already reads as the document (no
        // marker seen yet), stop echoing it into the bubble live.
        const text = live.doc !== null ? live.chat : isDocumentText(streamingText) ? '' : streamingText;
        return text ? <div className={cn(BUBBLE, 'rounded-bl-sm bg-muted text-ink')}>{renderMsg(text)}</div> : null;
      })()}
    </>
  );
}
